import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";
import logger from "../../utils/logger";

// ==================== ASYNC THUNKS ====================

/**
 * Fetch all categories
 */
export const fetchCategories = createAsyncThunk(
  "categories/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/categories");
      logger.debug("Categories fetched successfully", {
        count: response.data.totalResults,
      });
      return response.data.categories;
    } catch (error) {
      logger.error("Failed to fetch categories:", error);
      const message =
        error.response?.data?.message || "Failed to fetch categories";
      return rejectWithValue(message);
    }
  }
);

/**
 * Fetch single category by ID
 */
export const fetchCategoryById = createAsyncThunk(
  "categories/fetchById",
  async (categoryId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/categories/${categoryId}`);
      logger.debug("Category fetched successfully", { id: categoryId });
      return response.data;
    } catch (error) {
      logger.error("Failed to fetch category:", error, { categoryId });
      const message =
        error.response?.data?.message || "Failed to fetch category";
      return rejectWithValue(message);
    }
  }
);

// ==================== SLICE ====================

const initialState = {
  categories: [],
  currentCategory: null,
  loading: undefined, // true/false during fetches to distinguish from initial state
  error: null,
  lastFetched: null, // Timestamp for cache management
};

const categoriesSlice = createSlice({
  name: "categories",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentCategory: (state) => {
      state.currentCategory = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all categories
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
        state.lastFetched = Date.now();
        state.error = null;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch single category
      .addCase(fetchCategoryById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategoryById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCategory = action.payload;
        state.error = null;
      })
      .addCase(fetchCategoryById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// ==================== SELECTORS ====================

export const selectCategories = (state) => state.categories.categories;
export const selectCurrentCategory = (state) =>
  state.categories.currentCategory;
export const selectCategoriesLoading = (state) => state.categories.loading;
export const selectCategoriesError = (state) => state.categories.error;
export const selectLastFetched = (state) => state.categories.lastFetched;

// Get category by slug
export const selectCategoryBySlug = (slug) => (state) => {
  return state.categories.categories.find(
    (cat) =>
      cat.slug === slug || cat.name.toLowerCase().replace(/\s+/g, "-") === slug
  );
};

// Check if categories need refresh (older than 60 minutes)
export const selectNeedsCategoriesRefresh = (state) => {
  const lastFetched = state.categories.lastFetched;
  if (!lastFetched) return true;

  const sixtyMinutes = 60 * 60 * 1000;
  return Date.now() - lastFetched > sixtyMinutes;
};

// ==================== EXPORTS ====================

export const { clearError, clearCurrentCategory } = categoriesSlice.actions;
export default categoriesSlice.reducer;
